/**
 * DecisionFlow API Client
 *
 * Centralized API client with authentication, error handling,
 * and type-safe request/response handling.
 */

// =============================================================================
// Types
// =============================================================================

export interface ApiError {
  message: string;
  code: string;
  status: number;
  details?: Record<string, unknown>;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// Decision Types
export type DecisionStatus = 'pending' | 'approved' | 'rejected' | 'executing' | 'completed';
export type AgentType = 'capacity' | 'revenue' | 'risk' | 'timing' | 'coordination';
export type UrgencyLevel = 'low' | 'medium' | 'high' | 'critical';

export interface Decision {
  id: string;
  title: string;
  description: string;
  agent: AgentType;
  status: DecisionStatus;
  confidence: number;
  urgency: UrgencyLevel;
  impact: {
    revenue?: number;
    cost?: number;
    efficiency?: number;
    risk?: number;
  };
  options: DecisionOption[];
  recommendedOptionId: string;
  reasoning: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  assignedTo?: string;
  vertical: 'ops' | 'marketing' | 'sports';
}

export interface DecisionOption {
  id: string;
  label: string;
  description: string;
  confidence: number;
  pros: string[];
  cons: string[];
  projectedImpact: {
    revenue?: number;
    cost?: number;
    risk?: number;
    efficiency?: number;
    timeline?: string;
  };
}

// Analytics Types
export interface DashboardStats {
  activeDecisions: number;
  activeDecisionsChange: number;
  pendingApprovals: number;
  pendingApprovalsChange: number;
  teamCapacity: number;
  teamCapacityChange: number;
  revenueImpact: number;
  revenueImpactChange: number;
  successRate: number;
  successRateChange: number;
  avgResponseTime: number;
  avgResponseTimeChange: number;
}

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  avatar?: string;
  utilization: number;
  assignedDecisions: number;
  status: 'available' | 'busy' | 'away' | 'offline';
}

export interface CapacityData {
  teams: Array<{
    id: string;
    name: string;
    utilization: number;
    members: number;
    activeProjects: number;
  }>;
  overall: number;
}

export interface TrendDataPoint {
  date: string;
  decisions: number;
  approved: number;
  rejected: number;
  successRate: number;
}

export interface ActivityItem {
  id: string;
  type: 'decision_created' | 'decision_approved' | 'decision_rejected' | 'decision_executed' | 'alert' | 'milestone';
  title: string;
  description: string;
  timestamp: string;
  agent?: AgentType;
  user?: {
    name: string;
    avatar?: string;
  };
  metadata?: Record<string, unknown>;
}

export interface AgentStatus {
  agent: AgentType;
  status: 'active' | 'training' | 'idle' | 'error';
  lastAction: string;
  decisionsToday: number;
  accuracy: number;
  confidence: number;
}

// =============================================================================
// API Client
// =============================================================================

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8005/api/v1';

class ApiClient {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
  }

  clearToken() {
    this.token = null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        message: 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        status: response.status,
      }));
      throw error;
    }

    return response.json();
  }

  // =========================================================================
  // Decisions API
  // =========================================================================

  async getDecisions(params?: {
    status?: DecisionStatus;
    agent?: AgentType;
    vertical?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Decision>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
    }
    return this.request(`/decisions?${searchParams}`);
  }

  async getDecision(id: string): Promise<Decision> {
    return this.request(`/decisions/${id}`);
  }

  async approveDecision(id: string, optionId: string): Promise<Decision> {
    return this.request(`/decisions/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ optionId }),
    });
  }

  async rejectDecision(id: string, reason: string): Promise<Decision> {
    return this.request(`/decisions/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async getRecentDecisions(limit = 10): Promise<Decision[]> {
    return this.request(`/decisions/recent?limit=${limit}`);
  }

  // =========================================================================
  // Analytics API
  // =========================================================================

  async getDashboardStats(): Promise<DashboardStats> {
    return this.request('/analytics/dashboard');
  }

  async getCapacityData(): Promise<CapacityData> {
    return this.request('/analytics/capacity');
  }

  async getTrends(days = 30): Promise<TrendDataPoint[]> {
    return this.request(`/analytics/trends?days=${days}`);
  }

  async getActivity(limit = 20): Promise<ActivityItem[]> {
    return this.request(`/analytics/activity?limit=${limit}`);
  }

  async getAgentStatus(): Promise<AgentStatus[]> {
    return this.request('/analytics/agents');
  }

  async getTeamMembers(): Promise<TeamMember[]> {
    // TODO: Create team endpoint
    return Promise.resolve([]);
  }

  // =========================================================================
  // NLI API
  // =========================================================================

  async ask(query: string): Promise<{
    response: string;
    intent: string;
    data?: unknown;
    suggestions?: string[];
  }> {
    return this.request('/ask', {
      method: 'POST',
      body: JSON.stringify({ query }),
    });
  }
}

export const api = new ApiClient();
export default api;
